// API Configuration

/**
 * Get the base API URL based on environment
 * @returns {string}
 */
export function getApiBaseUrl() {
    // Check if we're running in browser
    if (typeof window !== 'undefined') {
        const hostname = window.location.hostname;
        const port = window.location.port;
        
        // Production deployment (no localhost)
        if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
            console.log('🌍 Production mode: Using serverless functions');
            return ''; // Use relative URLs for serverless functions
        }
        
        // Vercel dev server (port 3000) - use real API
        if (hostname === 'localhost' && port === '3000') {
            console.log('🔥 Vercel dev mode: Using real API endpoints');
            return ''; // Use relative URLs for serverless functions
        }
        
        // Vite dev/preview server (other ports) - use mock responses
        console.log(`🎭 Mock mode: ${hostname}:${port} (use 'npm run dev:vercel' for real API)`);
        return 'MOCK'; // Special flag for mock responses
    }
    
    // Fallback for server-side rendering
    console.log('🌍 Server-side: Using production serverless functions');
    return ''; // Empty string means relative URLs (/api/*)
}

/**
 * Get full API endpoint URL
 * @param {string} endpoint 
 * @returns {string}
 */
export function getApiUrl(endpoint) {
    const baseUrl = getApiBaseUrl();
    
    // Handle mock mode for local development/preview
    if (baseUrl === 'MOCK') {
        console.log(`Mock mode: ${endpoint}`);
        return 'MOCK'; // Special flag for mock responses
    }
    
    // For production serverless functions, use /api/ prefix
    const fullUrl = `/api${endpoint}`;
    
    console.log(`API URL for endpoint ${endpoint}: ${fullUrl}`);
    return fullUrl;
}

/**
 * Make API request with proper error handling
 * @param {string} endpoint 
 * @param {Object} options 
 * @returns {Promise<Object>}
 */
export async function makeApiRequest(endpoint, options = {}) {
    const url = getApiUrl(endpoint);
    
    // Handle mock mode for local development/preview
    if (url === 'MOCK') {
        console.log('🎭 MOCK MODE: Data not saved - use "npm run dev:vercel" for real API');
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Return mock success response with clear warning
        return {
            success: true,
            message: '✅ Form submitted successfully! (MOCK MODE - Data not saved)',
            dev_note: `🎭 MOCK: Use "npm run dev:vercel" on port 3000 for real Google Sheets integration`,
            warning: '⚠️ This is a mock response. Data was NOT saved to Google Sheets.',
            timestamp: new Date().toISOString()
        };
    }
    
    const defaultOptions = {
        headers: {
            'Content-Type': 'application/json',
        },
    };
    
    const requestOptions = {
        ...defaultOptions,
        ...options,
        headers: {
            ...defaultOptions.headers,
            ...options.headers,
        },
    };

    try {
        console.log(`Making API request to: ${url}`);
        console.log('Request options:', requestOptions);
        
        const response = await fetch(url, requestOptions);
        
        console.log('Response status:', response.status);
        console.log('Response headers:', Object.fromEntries(response.headers.entries()));
        
        if (!response.ok) {
            const errorText = await response.text();
            console.error('Response error text:', errorText);
            throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
        }
        
        const result = await response.json();
        console.log('API response:', result);
        return result;
    } catch (error) {
        console.error(`API request failed for ${url}:`, error);
        
        // Provide specific error messages for production
        if (error.message.includes('fetch') || error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
            throw new Error('Unable to connect to server. Please check your internet connection and try again.');
        }
        
        if (error.message.includes('CORS') || error.message.includes('Cross-Origin')) {
            throw new Error('Configuration error: Invalid API endpoint. Please contact support.');
        }
        
        if (error.message.includes('404')) {
            throw new Error('API endpoint not found. Please contact support if this issue persists.');
        }
        
        if (error.message.includes('500')) {
            throw new Error('Server error occurred. Please try again later or contact support.');
        }
        
        // If it's already a formatted error message, pass it through
        if (error.message.startsWith('HTTP error!') || 
            error.message.startsWith('Unable to connect') || 
            error.message.startsWith('Configuration error')) {
            throw error;
        }
        
        throw new Error('Failed to submit your message. Please check your connection and try again.');
    }
}
